import { trackForcedReload } from "./trackNavigation";

const LAST_CHUNK_RELOAD_TS_KEY = "leadsor_last_chunk_reload_ts";
const CHUNK_RELOAD_ATTEMPTS_KEY = "leadsor_chunk_reload_attempts";
/** Не пытаться перезагружать чаще, чем раз в этот интервал после предыдущей попытки (анти-петля). */
const MIN_MS_BETWEEN_RELOAD_ATTEMPTS = 8000;
/** Разрешаем максимум один авто-reload за сессию вкладки. */
const MAX_CHUNK_RELOAD_ATTEMPTS = 1;
let lastChunkReloadAttemptTs = 0;
let chunkReloadAttempts = 0;

function getReloadAttempts(): number {
  try {
    const value = Number(sessionStorage.getItem(CHUNK_RELOAD_ATTEMPTS_KEY) || "0");
    return Number.isFinite(value) ? value : 0;
  } catch {
    return chunkReloadAttempts;
  }
}

function isChunkLoadError(error: unknown): boolean {
  if (error == null) return false;
  if (typeof error === "object" && "name" in error && (error as Error).name === "ChunkLoadError") {
    return true;
  }
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : String(error);
  return (
    /Loading chunk [\d]+ failed/gi.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /'text\/html' is not a valid JavaScript MIME type/i.test(message)
  );
}

// Returns true if the failed resource is one of our own Vite asset chunks.
function isOwnChunkScript(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLScriptElement)) return false;
  try {
    const url = new URL(target.src);
    return url.origin === window.location.origin && url.pathname.startsWith("/assets/");
  } catch {
    return false;
  }
}

function shouldAttemptReload(): boolean {
  if (getReloadAttempts() >= MAX_CHUNK_RELOAD_ATTEMPTS) return false;
  try {
    const last = Number(sessionStorage.getItem(LAST_CHUNK_RELOAD_TS_KEY) || "0");
    return Date.now() - last >= MIN_MS_BETWEEN_RELOAD_ATTEMPTS;
  } catch {
    return Date.now() - lastChunkReloadAttemptTs >= MIN_MS_BETWEEN_RELOAD_ATTEMPTS;
  }
}

function markReloadAttempt(): void {
  const now = Date.now();
  const nextAttempt = getReloadAttempts() + 1;
  lastChunkReloadAttemptTs = now;
  chunkReloadAttempts = nextAttempt;
  try {
    sessionStorage.setItem(LAST_CHUNK_RELOAD_TS_KEY, String(now));
    sessionStorage.setItem(CHUNK_RELOAD_ATTEMPTS_KEY, String(nextAttempt));
  } catch {
    /* private mode / quota */
  }
}

function tryRecover(reason: unknown): boolean {
  if (!isChunkLoadError(reason)) return false;
  if (!shouldAttemptReload()) return false;
  markReloadAttempt();
  trackForcedReload("chunk_load_recovery", { error: reason });
  window.setTimeout(() => {
    window.location.reload();
  }, 0);
  return true;
}

/**
 * После деплоя старая вкладка может запросить несуществующий чанк — перезагружаем страницу один раз,
 * чтобы подтянуть новый index и манифест бандла.
 */
export function initChunkLoadRecovery(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("unhandledrejection", (event) => {
    if (tryRecover(event.reason)) {
      event.preventDefault();
    }
  });

  // Capture phase required — resource load errors (script/img/link) don't bubble.
  window.addEventListener("error", (event) => {
    if (isOwnChunkScript(event.target)) {
      tryRecover(Object.assign(new Error("Chunk script failed to load"), { name: "ChunkLoadError" }));
      return;
    }
    const payload = event.error ?? event.message;
    if (payload && tryRecover(payload)) {
      event.preventDefault();
    }
  }, true);
}
